// Phase 21C — Primary Order Config admin screen.
//
// Lets admins define orderable SKU allowlists + open/close windows per
// zone or per distributor. Distributor scope wins over zone scope for the
// same SKU (see backend `resolve_effective`).
//
// Screen composition:
//   - Filter bar: scope_type, scope_id
//   - Table listing all configs
//   - Create / Edit dialog with scope picker + SKU chips + datetime range
//   - "Preview effective SKUs for distributor X on date Y" helper
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, X, Loader2, ListFilter } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { SearchableSelect } from "@/components/ui/searchable-select";
import BulkActionsBar from "@/components/bulk/BulkActionsBar";
import PageHeader from "@/components/PageHeader";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(v) {
  if (!v) return null;
  return new Date(v).toISOString();
}

export default function PrimaryOrderConfig() {
  const [configs, setConfigs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [zones, setZones] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [skus, setSkus] = useState([]);

  const [scopeTypeFilter, setScopeTypeFilter] = useState("");
  const [scopeIdFilter, setScopeIdFilter] = useState("");

  const [editing, setEditing] = useState(null);   // config obj or "new"
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 200 };
      if (scopeTypeFilter) params.scope_type = scopeTypeFilter;
      if (scopeIdFilter) params.scope_id = scopeIdFilter;
      const r = await api.get("/primary-order-config", { params });
      setConfigs(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [scopeTypeFilter, scopeIdFilter]);

  useEffect(() => {
    (async () => {
      try {
        const [z, d, s] = await Promise.all([
          api.get("/zones"),
          api.get("/distributors", { params: { page_size: 2000 } }),
          api.get("/skus", { params: { page_size: 500, is_active: true } }),
        ]);
        setZones(z.data.items || z.data || []);
        setDistributors(d.data.items || []);
        setSkus(s.data.items || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  const zoneMap = useMemo(() => Object.fromEntries(zones.map(z => [z.id, z])), [zones]);
  const distMap = useMemo(() => Object.fromEntries(distributors.map(d => [d.id, d])), [distributors]);
  const skuMap = useMemo(() => Object.fromEntries(skus.map(s => [s.id, s])), [skus]);

  const scopeLabel = (cfg) => {
    if (cfg.scope_type === "zone") return zoneMap[cfg.scope_id]?.name || cfg.scope_id;
    return distMap[cfg.scope_id]?.name || cfg.scope_id;
  };

  const removeCfg = async (cfg) => {
    try {
      await api.delete(`/primary-order-config/${cfg.id}`);
      toast.success("Config removed");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="primary-order-config-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Primary Order Config" }]}
        title="Primary Order Config"
        subtitle="Business rules for primary orders raised on distributors."
        favorite={{ key: "admin.primary-order-config", type: "screen", label: "Primary Order Config", route: "/admin/primary-order-config" }}
        testid="primaryorderconfig-page-header"
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Primary Order Config</h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            Orderable SKU allowlist + open/close window. Distributor scope wins over Zone scope for the same SKU.
          </p>
        </div>
        <div className="flex gap-2">
          <BulkActionsBar
            masterKey="primary_order_config"
            label="Primary Order Config"
            onImportDone={() => load()}
          />
          <Button variant="outline" onClick={() => setPreviewOpen(true)}
                  data-testid="poc-preview-btn">
            <Eye className="h-4 w-4 mr-1" /> Preview effective
          </Button>
          <Button onClick={() => setEditing("new")}
                  className="tulsi-gradient text-white" data-testid="poc-new-btn">
            <Plus className="h-4 w-4 mr-1" /> New config
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          <div className="min-w-[160px]">
            <Label className="text-[11px] text-neutral-500">Scope Type</Label>
            <Select value={scopeTypeFilter || "all"}
                    onValueChange={v => { setScopeTypeFilter(v === "all" ? "" : v); setScopeIdFilter(""); }}>
              <SelectTrigger data-testid="poc-filter-scope-type"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="zone">Zone</SelectItem>
                <SelectItem value="distributor">Distributor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {scopeTypeFilter && (
            <div className="min-w-[260px]">
              <Label className="text-[11px] text-neutral-500">Scope</Label>
              {/* Type-to-search — a plain Select over 300+ distributors made
                  finding one by scrolling painful; SearchableSelect is the
                  same fix already used for Distributor pickers elsewhere. */}
              <SearchableSelect
                value={scopeIdFilter}
                onChange={setScopeIdFilter}
                options={(scopeTypeFilter === "zone" ? zones : distributors).map(x => ({
                  id: x.id, code: x.code, name: x.name,
                }))}
                placeholder="Any"
                emptyMessage="No match."
                testid="poc-filter-scope-id"
                clearable
              />
            </div>
          )}
          <div className="ml-auto text-xs text-neutral-500 flex items-center gap-1">
            <ListFilter className="h-3.5 w-3.5" />
            {total} config{total !== 1 ? "s" : ""}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-0">
          <Table data-testid="poc-table">
            <TableHeader>
              <TableRow>
                <TableHead>Scope</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Window</TableHead>
                <TableHead>SKUs</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" />
                  </TableCell>
                </TableRow>
              )}
              {!loading && configs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-neutral-400 text-sm">
                    No configs. Create one to control orderable SKUs and windows.
                  </TableCell>
                </TableRow>
              )}
              {configs.map(c => (
                <TableRow key={c.id} data-testid={`poc-row-${c.id}`}>
                  <TableCell className="font-medium text-neutral-900">{scopeLabel(c)}</TableCell>
                  <TableCell>
                    <Badge variant={c.scope_type === "distributor" ? "default" : "secondary"}
                           className={c.scope_type === "distributor" ? "bg-[#6D2C91] text-white" : ""}>
                      {c.scope_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>Open: {fmtDateTime(c.window_open_at)}</div>
                    <div>Close: {fmtDateTime(c.window_close_at)}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {(c.sku_ids || []).slice(0, 6).map(id => (
                        <Badge key={id} variant="outline" className="text-[10px]">
                          {skuMap[id]?.sku_code || id.slice(0, 6)}
                        </Badge>
                      ))}
                      {(c.sku_ids || []).length > 6 && (
                        <Badge variant="outline" className="text-[10px] bg-neutral-100">
                          +{c.sku_ids.length - 6} more
                        </Badge>
                      )}
                      {(c.sku_ids || []).length === 0 && (
                        <span className="text-[11px] text-neutral-400 italic">none</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={c.is_active ? "bg-emerald-100 text-emerald-800"
                                                   : "bg-neutral-100 text-neutral-500"}>
                      {c.is_active ? "active" : "inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => setEditing(c)}
                            data-testid={`poc-edit-${c.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(c)}
                            data-testid={`poc-delete-${c.id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && (
        <ConfigDialog editing={editing}
                      zones={zones} distributors={distributors} skus={skus}
                      onClose={() => setEditing(null)}
                      onSaved={() => { setEditing(null); load(); }} />
      )}

      {previewOpen && (
        <PreviewDialog distributors={distributors} skus={skus}
                       onClose={() => setPreviewOpen(false)} />
      )}

      <Dialog open={!!confirmDelete} onOpenChange={v => !v && setConfirmDelete(null)}>
        <DialogContent data-testid="poc-delete-dialog">
          <DialogHeader>
            <DialogTitle>Remove config?</DialogTitle>
            <DialogDescription>
              This immediately removes SKU restrictions for {confirmDelete ? scopeLabel(confirmDelete) : ""}. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700"
                    onClick={() => removeCfg(confirmDelete)}
                    data-testid="poc-delete-confirm">
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Create/Edit dialog -----------------------------------------------
function ConfigDialog({ editing, zones, distributors, skus, onClose, onSaved }) {
  const isNew = editing === "new";
  const [scopeType, setScopeType] = useState(isNew ? "zone" : editing.scope_type);
  const [scopeId, setScopeId] = useState(isNew ? "" : editing.scope_id);
  const [openAt, setOpenAt] = useState(isNew ? "" : isoToLocalInput(editing.window_open_at));
  const [closeAt, setCloseAt] = useState(isNew ? "" : isoToLocalInput(editing.window_close_at));
  const [isActive, setIsActive] = useState(isNew ? true : !!editing.is_active);
  const [selectedSkuIds, setSelectedSkuIds] = useState(new Set(isNew ? [] : editing.sku_ids || []));
  const [skuSearch, setSkuSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const filteredSkus = useMemo(() => {
    const q = skuSearch.trim().toLowerCase();
    if (!q) return skus;
    return skus.filter(s => (s.name || "").toLowerCase().includes(q)
                          || (s.sku_code || "").toLowerCase().includes(q));
  }, [skus, skuSearch]);

  const toggleSku = (id) => {
    setSelectedSkuIds(prev => {
      const nx = new Set(prev);
      nx.has(id) ? nx.delete(id) : nx.add(id);
      return nx;
    });
  };

  // Bulk-select acts on whatever the search box currently narrows the list
  // to — "Select all" with a filter typed in only adds the matching SKUs
  // (existing selections outside the filter are left alone), so an admin
  // can build up a big allowlist a category/search at a time instead of
  // clicking every checkbox by hand.
  const allFilteredSelected = filteredSkus.length > 0
    && filteredSkus.every(s => selectedSkuIds.has(s.id));
  const selectAllFiltered = () => {
    setSelectedSkuIds(prev => new Set([...prev, ...filteredSkus.map(s => s.id)]));
  };
  const clearAllFiltered = () => {
    setSelectedSkuIds(prev => {
      const nx = new Set(prev);
      filteredSkus.forEach(s => nx.delete(s.id));
      return nx;
    });
  };

  const scopeOptions = scopeType === "zone" ? zones : distributors;

  const save = async () => {
    if (!scopeId) { toast.error("Select a scope"); return; }
    if (openAt && closeAt && new Date(openAt) >= new Date(closeAt)) {
      toast.error("Open must be before Close"); return;
    }
    setSaving(true);
    try {
      const body = {
        scope_type: scopeType, scope_id: scopeId,
        sku_ids: Array.from(selectedSkuIds),
        window_open_at: localInputToIso(openAt),
        window_close_at: localInputToIso(closeAt),
        is_active: isActive,
      };
      if (isNew) await api.post("/primary-order-config", body);
      else await api.patch(`/primary-order-config/${editing.id}`, body);
      toast.success(isNew ? "Config created" : "Config updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto"
                     data-testid="poc-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New" : "Edit"} Primary Order Config</DialogTitle>
          <DialogDescription>
            Define which SKUs are orderable and when the ordering window is open.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Scope type</Label>
            <Select value={scopeType} onValueChange={(v) => { setScopeType(v); setScopeId(""); }}
                    disabled={!isNew}>
              <SelectTrigger data-testid="poc-scope-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="zone">Zone (broad)</SelectItem>
                <SelectItem value="distributor">Distributor (overrides zone)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{scopeType === "zone" ? "Zone" : "Distributor"}</Label>
            <SearchableSelect
              value={scopeId}
              onChange={setScopeId}
              options={scopeOptions.map(x => ({ id: x.id, code: x.code, name: x.name }))}
              placeholder="Select…"
              disabled={!isNew}
              clearable={false}
              testid="poc-scope-id"
            />
          </div>
          <div>
            <Label>Window open at</Label>
            <Input type="datetime-local" value={openAt}
                   onChange={e => setOpenAt(e.target.value)}
                   data-testid="poc-window-open" />
          </div>
          <div>
            <Label>Window close at</Label>
            <Input type="datetime-local" value={closeAt}
                   onChange={e => setCloseAt(e.target.value)}
                   data-testid="poc-window-close" />
          </div>
          <div className="flex items-center gap-3 md:col-span-2">
            <Switch checked={isActive} onCheckedChange={setIsActive}
                    data-testid="poc-active-toggle" />
            <Label className="text-sm">Active</Label>
          </div>
        </div>

        <div className="pt-2">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <Label className="text-sm font-semibold">
              Orderable SKUs ({selectedSkuIds.size} selected)
            </Label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" className="h-8 text-xs"
                      onClick={allFilteredSelected ? clearAllFiltered : selectAllFiltered}
                      disabled={filteredSkus.length === 0}
                      data-testid="poc-sku-select-all">
                {allFilteredSelected
                  ? `Unselect all${skuSearch ? " (filtered)" : ""}`
                  : `Select all${skuSearch ? " (filtered)" : ""}`}
              </Button>
              <Input placeholder="Search SKUs…" value={skuSearch}
                     onChange={e => setSkuSearch(e.target.value)}
                     className="w-64 h-8 text-sm" data-testid="poc-sku-search" />
            </div>
          </div>

          {selectedSkuIds.size > 0 && (
            <div className="mb-2 flex flex-wrap gap-1 border rounded-lg p-2 bg-neutral-50 max-h-32 overflow-y-auto"
                 data-testid="poc-selected-chips">
              {Array.from(selectedSkuIds).map(id => {
                const sku = skus.find(s => s.id === id);
                return (
                  <button key={id} onClick={() => toggleSku(id)} type="button"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#6D2C91] text-white text-[11px]"
                          data-testid={`poc-chip-${sku?.sku_code || id.slice(0,6)}`}>
                    {sku?.sku_code || id.slice(0, 6)}
                    <X className="h-3 w-3" />
                  </button>
                );
              })}
            </div>
          )}

          <div className="border rounded-lg max-h-56 overflow-y-auto" data-testid="poc-sku-list">
            {filteredSkus.map(s => {
              const on = selectedSkuIds.has(s.id);
              return (
                <label key={s.id} className={
                  "flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-neutral-50 border-b last:border-0 " +
                  (on ? "bg-[#6D2C91]/5" : "")
                }
                       data-testid={`poc-sku-option-${s.sku_code}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleSku(s.id)}
                         className="accent-[#6D2C91]" />
                  <div className="flex-1">
                    <div className="text-sm text-neutral-900">{s.name}</div>
                    <div className="text-[10px] text-neutral-500 font-mono">
                      {s.sku_code}
                      {s.pieces_per_case ? ` · case pack ${s.pieces_per_case}` : ""}
                    </div>
                  </div>
                </label>
              );
            })}
            {filteredSkus.length === 0 && (
              <div className="p-4 text-center text-xs text-neutral-400">No SKUs match "{skuSearch}"</div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}
                  className="tulsi-gradient text-white"
                  data-testid="poc-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Preview effective dialog -----------------------------------------
function PreviewDialog({ distributors, skus, onClose }) {
  const [distId, setDistId] = useState("");
  const [atLocal, setAtLocal] = useState(isoToLocalInput(new Date().toISOString()));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const skuMap = useMemo(() => Object.fromEntries(skus.map(s => [s.id, s])), [skus]);

  const run = async () => {
    if (!distId) { toast.error("Select a distributor"); return; }
    setLoading(true);
    try {
      const r = await api.get("/primary-order-config/preview", {
        params: { distributor_id: distId, at: localInputToIso(atLocal) || undefined },
      });
      setResult(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="poc-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview effective config</DialogTitle>
          <DialogDescription>
            Resolves the union of zone + distributor configs (distributor wins) at a given point in time.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Distributor</Label>
            <SearchableSelect
              value={distId}
              onChange={setDistId}
              options={distributors.map(d => ({ id: d.id, code: d.code, name: d.name }))}
              placeholder="Select…"
              clearable={false}
              testid="poc-preview-dist"
            />
          </div>
          <div>
            <Label>At (local)</Label>
            <Input type="datetime-local" value={atLocal}
                   onChange={e => setAtLocal(e.target.value)}
                   data-testid="poc-preview-at" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={run} disabled={loading} className="tulsi-gradient text-white"
                  data-testid="poc-preview-run">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Resolve
          </Button>
        </div>

        {result && (
          <div className="border rounded-lg p-3 space-y-2 bg-neutral-50" data-testid="poc-preview-result">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-neutral-500">In window</div>
                <Badge className={result.in_window ? "bg-emerald-100 text-emerald-800"
                                                   : "bg-red-100 text-red-800"}>
                  {result.in_window ? "OPEN" : "CLOSED"}
                </Badge>
              </div>
              <div>
                <div className="text-neutral-500">Opens</div>
                <div>{fmtDateTime(result.window_open_at)}</div>
              </div>
              <div>
                <div className="text-neutral-500">Closes</div>
                <div>{fmtDateTime(result.window_close_at)}</div>
              </div>
            </div>
            {result.no_config && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                No config matches. All SKUs orderable, no window restriction.
              </div>
            )}
            <div>
              <div className="text-xs text-neutral-500 mb-1">Effective SKUs ({result.sku_ids.length})</div>
              <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                {result.sku_ids.map(id => {
                  const src = (result.source_map || {})[id];
                  return (
                    <Badge key={id} variant="outline"
                           className={"text-[10px] " + (src === "distributor"
                             ? "border-[#6D2C91] text-[#6D2C91]"
                             : "border-neutral-300 text-neutral-700")}
                           title={`source: ${src || "?"}`}>
                      {skuMap[id]?.sku_code || id.slice(0, 6)}
                      <span className="ml-1 opacity-60">({src?.[0]})</span>
                    </Badge>
                  );
                })}
                {result.sku_ids.length === 0 && (
                  <span className="text-xs text-neutral-400 italic">none</span>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
